import { Button } from '@client/components/ui/button';
import { Badge } from '@client/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@client/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@client/components/ui/alert';
import { Plus, Eye, AlertCircle } from 'lucide-react';
import { DataTable, TableColumn } from '@client/components/shared/DataTable';
import { ProfilePagination } from '@client/components/profile-manager/ProfilePagination';
import { LinkedInSourceLink } from './LinkedInSourceLink';
import type { List } from '@shared/types';

interface ListPaginationMeta {
  total: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  startIndex: number;
  endIndex: number;
}

interface ListOverviewProps {
  lists: List[];
  isLoading: boolean;
  error: Error | null;
  pagination: ListPaginationMeta | null;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
  onCreateList: () => void;
  onViewList: (list: List) => void;
  onEditList: (list: List) => void;
  onDeleteList: (list: List) => void;
  onViewProfiles: (list: List) => void;
  formatDate: (date: string) => string;
  getSourceBadge: (source: string) => React.ReactNode;
}

export function ListOverview({
  lists,
  isLoading,
  error,
  pagination,
  onPageChange,
  onLimitChange,
  onCreateList,
  onViewList,
  onEditList,
  onDeleteList,
  onViewProfiles,
  formatDate,
  getSourceBadge,
}: ListOverviewProps) {
  const totalProfiles = lists.reduce((sum, list) => sum + (list.profileCount || 0), 0);

  const columns: TableColumn<List>[] = [
    {
      key: 'name',
      header: 'List Name',
      render: (list) => (
        <div className="space-y-2">
          {/* List Name (clickable) */}
          <div>
            <Button
              variant="link"
              className="p-0 h-auto font-medium text-left hover:underline"
              onClick={() => onViewList(list)}
            >
              {list.name}
            </Button>
            
            {/* Description */}
            {list.description && (
              <div className="text-sm text-muted-foreground truncate max-w-md mt-1">
                {list.description}
              </div>
            )}
          </div>
          
          {/* LinkedIn Source Link (if available) */}
          {list.sourceUrl && (
            <div>
              <LinkedInSourceLink 
                list={list} 
                variant="badge"
                showLabel={true}
              />
            </div>
          )}
          
          {/* Query Params Preview (max 3) */}
          {list.queryParams && Object.keys(list.queryParams).length > 0 && (
            <div className="flex flex-wrap gap-1">
              {Object.entries(list.queryParams).slice(0, 3).map(([key, value]) => (
                <Badge key={key} variant="outline" className="text-xs">
                  {key}: {String(value).slice(0, 20)}
                  {String(value).length > 20 && '...'}
                </Badge>
              ))}
              {Object.keys(list.queryParams).length > 3 && (
                <Badge variant="outline" className="text-xs">
                  +{Object.keys(list.queryParams).length - 3} more
                </Badge>
              )}
            </div>
          )}
        </div>
      ),
    },
    {
      key: 'profileCount',
      header: 'Profiles',
      render: (list) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onViewProfiles(list)}
          disabled={list.profileCount === 0}
          className="hover:bg-primary/10"
        >
          <Badge variant="secondary" className="cursor-pointer">
            {list.profileCount || 0}
          </Badge>
        </Button>
      ),
    },
    {
      key: 'source',
      header: 'Source',
      render: (list) => (
        <div className="flex items-center gap-2">
          {getSourceBadge(list.source)}
        </div>
      ),
    },
    {
      key: 'updatedAt',
      header: 'Last Updated',
      className: 'text-sm text-muted-foreground',
      render: (list) => formatDate(list.updatedAt),
    },
  ];

  if (error) {
    return (
      <div className="p-6">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error Loading Lists</AlertTitle>
          <AlertDescription>
            {error instanceof Error ? error.message : 'Failed to load lists'}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <header className="bg-white dark:bg-gray-900 shadow-sm border-b border-slate-200 dark:border-gray-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 dark:text-slate-200">
              Profile Lists
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              {pagination?.total ?? lists.length} lists • {totalProfiles} profiles total
            </p>
          </div>
          <Button onClick={onCreateList}>
            <Plus className="h-4 w-4 mr-2" />
            Create List
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <div className="px-6 pb-6">
        <Card>
          <CardHeader>
            <CardTitle>All Lists</CardTitle>
            <CardDescription>
              Manage your profile lists. Lists can be created manually or automatically via LinkedIn Scraper.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <DataTable
              data={lists}
              columns={columns}
              loading={isLoading}
              getRowKey={(list) => list.id}
              emptyState={{
                icon: <Plus className="h-12 w-12 opacity-50" />,
                title: 'No lists available',
                description: 'Lists are created automatically through the LinkedIn Scraper or manually',
              }}
              actions={{
                onEdit: onEditList,
                onDelete: onDeleteList,
                custom: [
                  {
                    icon: <Eye className="h-4 w-4" />,
                    label: 'View Profiles',
                    onClick: onViewProfiles,
                    variant: 'ghost',
                    disabled: (list) => (list.profileCount || 0) === 0
                  }
                ]
              }}
              deleteConfirmation={{
                title: 'Delete List?',
                description: (list) =>
                  `Are you sure you want to delete the list "${list.name}"? This will remove ${list.profileCount || 0} profile(s) from this list and cannot be undone.`,
              }}
            />

            {/* Pagination */}
            {pagination && pagination.totalPages > 1 && (
              <ProfilePagination
                pagination={pagination}
                onPageChange={onPageChange}
                onLimitChange={onLimitChange}
                entityName="lists"
              />
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}